import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { DefaultGraphStyle } from '@models/graphs';
import { DeviceFlagsService } from '@service/device-flags.service';
import { AlarmLevel } from 'ops/common/models/alarm-level';
import { Channel, ChannelFns, FlagOption } from 'ops/common/models/channel';
import { ECharts, EChartsOption } from 'echarts';

const alarmLevelBarColor = new Map();
alarmLevelBarColor.set(AlarmLevel.Warning, DefaultGraphStyle.alarmWarning);
alarmLevelBarColor.set(AlarmLevel.Caution, DefaultGraphStyle.alarmCaution);
alarmLevelBarColor.set(AlarmLevel.Alarm, DefaultGraphStyle.alarm);

const alarmLevelBarHoverColor = new Map();
alarmLevelBarHoverColor.set(AlarmLevel.Warning, DefaultGraphStyle.alarmWarningHover);
alarmLevelBarHoverColor.set(AlarmLevel.Caution, DefaultGraphStyle.alarmCautionHover);
alarmLevelBarHoverColor.set(AlarmLevel.Alarm, DefaultGraphStyle.alarmHover);

@Component({
    selector: 'app-custom-bar-graph',
    templateUrl: './custom-bar-graph.component.html',
    styleUrls: ['./custom-bar-graph.component.scss'],
    standalone: false,
})
export class CustomBarGraphComponent implements OnChanges, OnInit {
    readonly echartsExtentions: any[];
    echartsOptions: EChartsOption = {};
    chart: ECharts = null;

    @Input() channel: Channel;
    @Input() divisions: number = 0;
    @Input() inEditMode: boolean = false;
    @Output() onRemoveItem: EventEmitter<void> = new EventEmitter();

    alarmBarColorMap = new Map();
    graphYAxisMax = 0;
    channelValue = undefined;

    statusString: string = '';
    status: FlagOption = FlagOption.UNDEFINED;

    constructor(private flagService: DeviceFlagsService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.channel) {
            this.initialAlarmBgColor();
        }
    }

    ngOnInit() {
        this.channelValue = this.flagService.getFlags(this.channel)?.value;

        this.flagService.onAllFlagsUpdated().subscribe(() => {
            this.channelValue = this.flagService.getFlags(this.channel)?.value;
            this.updateStatusString();
        });
        this.initialAlarmBgColor();
        this.findMaxYAxis();
    }

    onChartInit(event: ECharts) {
        this.chart = event;
    }

    updateStatusString() {
        this.status = this.flagService.getStatus(this.channel);
        this.statusString = ChannelFns.getStatusString(this.status);
    }

    checkChannelValue() {
        return this.channelValue === undefined;
    }

    isFailed() {
        return (
            this.flagService.isFailedUnder(this.channel) ||
            this.flagService.isFailedOver(this.channel)
        );
    }

    initialAlarmBgColor() {
        this.alarmBarColorMap = new Map();
        this.alarmBarColorMap.set(this.channel.fcuData.fcuChannelId, this.createMap(this.channel));
    }

    calculateHeight() {
        return (this.channelValue / this.graphYAxisMax) * 100;
    }

    getBarGraphColor() {
        const channelStatus = this.alarmBarColorMap
            .get(this.channel.fcuData.fcuChannelId)
            ?.get(this.status);

        if (channelStatus) return channelStatus[0];
    }

    createMap(channel: Channel) {
        let map = new Map();

        map.set(FlagOption.LOW_LOW, [
            alarmLevelBarColor.get(channel.fcuData.alarmSettingVeryLow.alarmLevel),
            alarmLevelBarHoverColor.get(channel.fcuData.alarmSettingVeryLow.alarmLevel),
        ]);
        map.set(FlagOption.LOW, [
            alarmLevelBarColor.get(channel.fcuData.alarmSettingLow.alarmLevel),
            alarmLevelBarHoverColor.get(channel.fcuData.alarmSettingLow.alarmLevel),
        ]);
        map.set(FlagOption.NORMAL, [
            DefaultGraphStyle.alarmNormal,
            DefaultGraphStyle.alarmNormalHover,
        ]);
        map.set(FlagOption.HIGH, [
            alarmLevelBarColor.get(channel.fcuData.alarmSettingHigh.alarmLevel),
            alarmLevelBarHoverColor.get(channel.fcuData.alarmSettingHigh.alarmLevel),
        ]);
        map.set(FlagOption.HIGH_HIGH, [
            alarmLevelBarColor.get(channel.fcuData.alarmSettingVeryHigh.alarmLevel),
            alarmLevelBarHoverColor.get(channel.fcuData.alarmSettingVeryHigh.alarmLevel),
        ]);
        map.set(FlagOption.UNDEFINED, [
            DefaultGraphStyle.alarmDefault,
            DefaultGraphStyle.alarmDefaultHover,
        ]);

        return map;
    }

    findMaxYAxis() {
        this.graphYAxisMax = Number(this.getScaledValue(this.channel.fcuData.measurableRange.max));
    }

    getScaledValue(value) {
        const scale = this.channel.fcuData.scale;
        const decimalPlaces = scale.toString().length - 1;
        const result = value / Math.pow(10, decimalPlaces);
        return result.toFixed(decimalPlaces);
    }

    removeItem() {
        this.onRemoveItem.emit();
    }
}
